import {
  searchCriteria,
  updateState,
  getState,
  emptyMatchingState,
  getFullRoles,
} from '@/services/matchingService';
import { getProject, updateProject } from '@/services/projectService';
import moment from 'moment';
import { hasSession } from '@/services/clientSessionService';
import { getVAMatches } from '@/services/virtualAssistantsService';
import { defineStore } from 'pinia';

export const useCheckoutStore = defineStore('checkout', {
  state: () => ({
    selectedKeyword: '',
    roles: [],
    selectedRoles: [],
    selectedTools: [],
    selectedSkills: [],
    matchingCriteria: {
      addons: { selectedInitialAddons: [], selectedRecurringAddons: [] },
    },
    timeStamp: null,
    ready: false,
    assistantsObj: {
      assistants: [],
      count: 0,
    },
    localRoles: [],
    isInDialog: false,
    assistantPagination: {
      assistantsPage: 1,
      assistantPerPage: 12,
    },
    specialVGUser: {
      uuid: -1,
      isSpecial: true,
      avatar: '/img/Virtual-Gurus-Assistants-Color-Logo-mobile.png',
      display_name: `Help me decide`,
      blurb: `Book a call with our Solutions team and they’ll help you find the best assistant(s) for your needs.
`,
    },
    checkoutMetadata: null,
  }),
  getters: {
    allRoles() {
      return this.roles;
    },
    enteredKeywords() {
      return this.matchingCriteria?.keywords;
    },
    flattedEnteredKeywords() {
      return (
        [
          this.matchingCriteria?.keywords?.roles || [],
          this.matchingCriteria?.keywords?.skills || [],
          this.matchingCriteria?.keywords?.tools || [],
          this.matchingCriteria?.keywords?.default || [],
        ].flat() || []
      );
    },
    assistantsArrayOfId() {
      return this.assistantsObj.assistants.map((assistant) => assistant.uuid);
    },
  },
  actions: {
    keepCartAlive() {
      setInterval(() => {
        if (!this.timeStamp) {
          return;
        }
        const currentState = getState();
        const timeStamp = moment().format('YYYYMMDDhhmmss');

        updateState({
          ...currentState,
          timeStamp,
        });

        this.timeStamp = timeStamp;
      }, 300000);
    },
    async initialSetup(payload) {
      const uuid = payload?.params?.uuid || getState()?.query?.uuid;
      try {
        if (uuid && hasSession()) {
          await this.loadProjectByUUID(uuid);
          await this.loadCriteriaByIds(this.matchingCriteria?.rids);

          this.ready = true;
        } else {
          await this.updateMatchingCriteria({
            ...getState().query,
          });

          if (this.matchingCriteria?.rids?.length > this.roles) {
            await this.searchRoles();
            await this.loadCriteriaByIds(this.matchingCriteria?.rids);
          }
        }
        if (
          this.roles.length === 0 &&
          this.flattedEnteredKeywords.length !== 0
        ) {
          await this.searchRoles();
        }

        if (this.matchingCriteria?.tids?.length !== 0) {
          const result = await searchCriteria([
            `ids=${this.matchingCriteria?.tids}`,
            `types[]=tool`,
          ]).then((data) =>
            data.map((item) => ({ id: item.id, keyword: item.value }))
          );
          this.setSelectedTools(result);
        }

        if (this.matchingCriteria?.sids?.length !== 0) {
          const result = await searchCriteria([
            `ids=${this.matchingCriteria?.sids}`,
            `types[]=skill`,
          ]).then((data) =>
            data.map((item) => ({ id: item.id, keyword: item.value }))
          );
          this.setSelectedSkills(result);
        }

        this.ready = true;
      } catch (error) {
        this.ready = true;
        throw new Error(
          error.message || 'something went wrong try again later'
        );
      }
    },
    async loadCriteriaByIds(payload) {
      this.selectedRoles = await searchCriteria([
        `ids=${payload}`,
        `types[]=role`,
      ]).catch(() => {
        return [];
      });
    },
    async selectKeyword(payload) {
      this.addKeyword(payload);
      let tids = this.matchingCriteria?.tids || [];
      let sids = this.matchingCriteria?.sids || [];

      if (payload.criteria === 'tools') {
        tids =
          !this.matchingCriteria?.tids ||
          !tids.find((tid) => tid?.id === payload.id)
            ? [...(this.matchingCriteria?.tids || []), payload.id]
            : [...(this.matchingCriteria?.tids || [])];
        this.toggleSelectedTools(payload);
      }

      if (payload.criteria === 'skills') {
        sids =
          !this.matchingCriteria?.sids ||
          !sids.find((sid) => sid.id === payload.id)
            ? [...(this.matchingCriteria?.sids || []), payload.id]
            : [...(this.matchingCriteria?.sids || [])];
      }

      await this.updateMatchingCriteria({
        ...this.matchingCriteria,
        tids,
        sids,
      });

      this.selectedKeyword = payload.keyword;
    },
    async getLocalRoles() {
      const result = await getFullRoles().catch(() => {
        return [];
      });
      this.localRoles = result;
    },
    async searchRoles() {
      const result = await Promise.all(
        this.flattedEnteredKeywords.map((keyword) => {
          return searchCriteria([
            `searchQuery=${encodeURIComponent(keyword)}`,
            `types[]=role`,
          ])
            .then((response) =>
              response.map((role) => ({ ...role, keywords: [keyword] }))
            )
            .catch(() => {
              return [];
            });
        })
      );

      if (result.flat().length === 0) {
        if (this.localRoles.length === 0) {
          await this.getLocalRoles();
        }
        this.roles = [];
      } else {
        this.roles = [...result.flat(), ...this.selectedRoles].reduce(
          (accumulator, role) => {
            const existing = accumulator.find(
              (selfRole) => role.id === selfRole.id
            );
            if (existing) {
              existing.keywords = [
                ...new Set([...existing.keywords, ...(role.keywords || [])]),
              ];
            } else {
              accumulator.push(role);
            }
            return accumulator;
          },
          []
        );
      }
    },
    async loadProjectByUUID(payload) {
      const result = await getProject(payload).catch(() => {});

      if (result.project) {
        this.updateMatchingCriteriaLocally({
          ...result.project.matching_criteria,
          uuid: result.project.uuid,
          project_name: result.project.project_name,
        });
        if (result.project.checkoutMetadata) {
          this.checkoutMetadata = result.project.checkoutMetadata;
        }
      } else {
        this.removeMatchingCriteria();
        throw new Error(
          'Draft subscription has been deleted. Please start a new search.'
        );
      }
    },
    async updateMatchingCriteria(payload) {
      if (payload.uuid && !this.isInDialog) {
        await updateProject({
          uuid: payload.uuid,
          matching_criteria: payload,
        }).catch(() => {});
      }
      if (
        this.assistantPagination.assistantsPage !== 1 &&
        (JSON.stringify(this.matchingCriteria.focusedRid) !==
          JSON.stringify(payload.focusedRid) ||
          JSON.stringify(this.matchingCriteria.hours) !==
            JSON.stringify(payload.hours) ||
          JSON.stringify(this.matchingCriteria.keywords) !==
            JSON.stringify(payload.keywords) ||
          JSON.stringify(this.matchingCriteria.rids) !==
            JSON.stringify(payload.rids) ||
          this.matchingCriteria.self_identifications !==
            payload.self_identifications ||
          JSON.stringify(this.matchingCriteria.sids) !==
            JSON.stringify(payload.sids) ||
          JSON.stringify(this.matchingCriteria.tids) !==
            JSON.stringify(payload.tids) ||
          this.matchingCriteria.timezone !== payload.timezone ||
          this.matchingCriteria.timezone_offset !== payload.timezone_offset ||
          this.matchingCriteria.offset_range !== payload.offset_range)
      ) {
        this.updateAssistantPage(1);
      }
      this.updateMatchingCriteriaLocally(payload);
    },
    loadSelectedKeywords() {
      this.keywords = getState()?.query?.keywords || [];
    },
    async searchVAs(payload) {
      // while editing roles in the dialog, there's a period of time where rids and hours don't line up - in that case, don't search and clear assistants
      if (payload.hours?.length !== payload.rids?.length) {
        this.assistantsObj = {
          assistants: [],
          count: 0,
        };
        return;
      }
      this.assistantsObj = await getVAMatches(
        new URLSearchParams(JSON.parse(JSON.stringify(payload))).toString()
      ).catch(() => {});
    },
    async immediateCheckout() {
      let cart = [];
      this.selectedRoles.forEach((role) => {
        if (
          !this.matchingCriteria?.cart?.find(
            (cartItem) => cartItem.role_id === role.id
          )
        ) {
          cart = [
            ...cart,
            {
              hours: this.matchingCriteria.hours[
                this.matchingCriteria.rids.indexOf(role.id)
              ],
              role_id: role.id,
              assistant_id: -1,
              avatar_id: -1,
              known_as: this.specialVGUser.display_name,
              tids: this.matchingCriteria.tids,
              sids: this.matchingCriteria.sids,
            },
          ];
        }
      });
      await this.updateMatchingCriteria({
        ...this.matchingCriteria,
        cart: [...(this.matchingCriteria.cart || []), ...cart],
      });
    },
    addKeyword(payload) {
      if (
        this.matchingCriteria.keywords?.payload?.criteria.find(
          (keyword) => keyword === payload.keyword
        )
      ) {
        return;
      }
      if (this.matchingCriteria.keywords?.[payload?.criteria]) {
        this.matchingCriteria.keywords[payload?.criteria].push(payload.keyword);
      } else {
        this.matchingCriteria.keywords = {
          ...this.matchingCriteria.keywords,
          [payload.criteria]: [payload.keyword],
        };
      }

      const currentState = getState();
      if (!this.isInDialog) {
        updateState({
          ...currentState,
          query: this.matchingCriteria,
        });
      }
    },
    setSelectedTools(payload) {
      this.selectedTools = payload;
    },
    setSelectedSkills(payload) {
      this.selectedSkills = payload;
    },
    toggleSelectedRoles(payload) {
      const currentState = getState();
      if (this.matchingCriteria?.rids?.find((rid) => rid === payload.id)) {
        const existingIndex = this.matchingCriteria.rids.findIndex(
          (rid) => rid === payload.id
        );
        const existingIndexForFocusedRid = this.matchingCriteria?.focusedRid?.findIndex(
          (rid) => rid === payload.id
        );

        this.matchingCriteria.rids.splice(existingIndex, 1);
        this.matchingCriteria.hours?.splice(existingIndex, 1);
        if (existingIndexForFocusedRid > -1) {
          this.matchingCriteria.focusedRid.splice(
            existingIndexForFocusedRid,
            1
          );
        }
        this.selectedRoles?.splice(
          this.selectedRoles.findIndex((role) => role.id === payload.id),
          1
        );
        if (!this.isInDialog) {
          updateState({
            ...currentState,
            query: this.matchingCriteria,
          });
        }
      } else {
        this.selectedRoles.push(payload);
        if (this.matchingCriteria?.rids) {
          this.matchingCriteria?.rids.push(payload.id);
          this.matchingCriteria?.focusedRid?.push(payload.id);
        } else {
          this.matchingCriteria['rids'] = [payload.id];
          this.matchingCriteria['focusedRid'] = [payload.id];
        }
        if (!this.isInDialog) {
          updateState({
            ...currentState,
            query: this.matchingCriteria,
          });
        }
      }
      if (this.assistantPagination.assistantsPage !== 1) {
        this.assistantPagination.assistantsPage = 1;
      }
    },
    toggleSelectedTools(payload) {
      const currentState = getState();

      if (
        this.matchingCriteria?.tids?.find((tid) => tid === payload.id) ||
        this.selectedTools?.find(
          (tool) => tool.id === payload.id || tool.keyword === payload.value
        )
      ) {
        const existingIndex = this.matchingCriteria.tids.findIndex(
          (tid) => tid === payload.id
        );

        this.matchingCriteria.tids.splice(existingIndex, 1);

        this.selectedTools?.splice(
          this.selectedTools.findIndex(
            (tool) => tool.id === payload.id || tool.value === payload.value
          ),
          1
        );
        if (!this.isInDialog) {
          updateState({
            ...currentState,
            query: this.matchingCriteria,
          });
        }
      } else if (payload.keyword && payload.id) {
        this.selectedTools.push(payload);
        if (this.matchingCriteria?.tids) {
          this.matchingCriteria?.tids.push(payload.id);
        } else {
          this.matchingCriteria['tids'] = [payload.id];
        }
        if (!this.isInDialog) {
          updateState({
            ...currentState,
            query: this.matchingCriteria,
          });
        }
      }
    },
    toggleSelectedSkills(payload) {
      const currentState = getState();
      if (
        this.matchingCriteria?.sids?.find((sid) => sid === payload.id) ||
        this.selectedSkills?.find(
          (tool) => tool.id === payload.id || tool.keyword === payload.value
        )
      ) {
        const existingIndex = this.matchingCriteria.sids.findIndex(
          (sid) => sid === payload.id
        );

        this.matchingCriteria.sids.splice(existingIndex, 1);

        this.selectedSkills?.splice(
          this.selectedSkills.findIndex(
            (tool) => tool.id === payload.id || tool.value === payload.value
          ),
          1
        );
        if (!this.isInDialog) {
          updateState({
            ...currentState,
            query: this.matchingCriteria,
          });
        }
      } else if (payload.keyword && payload.id) {
        this.selectedSkills.push(payload);
        if (this.matchingCriteria?.sids) {
          this.matchingCriteria?.sids.push(payload.id);
        } else {
          this.matchingCriteria['sids'] = [payload.id];
        }
        if (!this.isInDialog) {
          updateState({
            ...currentState,
            query: this.matchingCriteria,
          });
        }
      }
    },
    updateMatchingCriteriaLocally(payload) {
      const currentState = getState();
      const timeStamp = this.timeStamp ?? moment().format('YYYYMMDDhhmmss');

      if (!this.isInDialog) {
        updateState({
          ...currentState,
          timeStamp,
          query: {
            ...payload,
          },
        });
      }

      this.matchingCriteria = payload;
      this.timeStamp = timeStamp;
    },
    removeMatchingCriteria() {
      this.selectedKeyword = '';
      this.matchingCriteria = {};
      this.roles = [];
      this.selectedRoles = [];
      this.assistantsObj = {
        assistants: [],
        count: 0,
      };
      emptyMatchingState();
    },
    async setSelectedPackage(payload) {
      await this.updateMatchingCriteria({
        ...this.matchingCriteria,
        package: payload,
      });
    },
    setIsInModal(payload) {
      this.isInDialog = payload;
    },
    updateAssistantPage(payload) {
      this.assistantPagination.assistantsPage = payload;
    },
  },
});
